/**
 * Modal de création d'un salarié
 * Création minimale avec validation temps réel
 * - Date de fin obligatoire pour CDD/Alternance/Stage
 * - Département optionnel
 * - Focus auto sur Prénom
 */

import { useState, useEffect, useCallback, useRef, forwardRef } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Loader2, UserPlus, AlertCircle, Info } from "lucide-react";
import { DatePickerFr } from "../../components/ui/date-picker-fr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Contract types that require end date
const FIXED_TERM_CONTRACTS = ["cdd", "alternance", "stage"];

// Initial form state
const INITIAL_FORM = {
  first_name: "",
  last_name: "",
  job_title: "",
  department: "",
  contract_type: "",
  employee_status: "",
  hire_date: "",
  end_date: "",
};

// Validation messages
const VALIDATION_MESSAGES = {
  first_name: "Le prénom est requis",
  last_name: "Le nom est requis",
  job_title: "L'intitulé du poste est requis",
  contract_type: "Le type de contrat est requis",
  employee_status: "Le statut est requis",
  hire_date: "La date d'embauche est requise",
  end_date: "La date de fin est requise pour ce type de contrat",
};

// ============================================================================
// FIELD COMPONENTS - Defined OUTSIDE the main component to prevent re-creation
// ============================================================================

// Input field component with validation
const FormField = forwardRef(({ 
  name, 
  label, 
  value, 
  error, 
  touched,
  onChange, 
  onBlur, 
  type = "text", 
  placeholder, 
  required = true 
}, ref) => (
  <div className="space-y-1.5">
    <Label htmlFor={name} className="text-sm font-medium text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <Input
      ref={ref}
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
      onBlur={() => onBlur(name)}
      placeholder={placeholder}
      className={`${error && touched ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
      data-testid={`input-${name}`}
    />
    {error && touched && (
      <p className="text-xs text-red-500 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        {error}
      </p>
    )}
  </div>
));
FormField.displayName = "FormField";

// Select field component with validation
const SelectField = ({ 
  name, 
  label, 
  value,
  error,
  touched,
  onChange,
  onBlur,
  placeholder, 
  options, 
  required = true 
}) => (
  <div className="space-y-1.5">
    <Label htmlFor={name} className="text-sm font-medium text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <Select
      value={value}
      onValueChange={(val) => {
        onChange(name, val);
        onBlur(name);
      }}
    >
      <SelectTrigger
        id={name}
        className={`${error && touched ? 'border-red-300 focus:border-red-500' : ''}`}
        data-testid={`select-${name}`}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map(opt => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    {error && touched && (
      <p className="text-xs text-red-500 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        {error}
      </p>
    )}
  </div>
);

// Date field component with French date picker
const DateField = ({ 
  name, 
  label, 
  value,
  error,
  touched,
  onChange,
  onBlur,
  required = true 
}) => (
  <div className="space-y-1.5">
    <Label htmlFor={name} className="text-sm font-medium text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <DatePickerFr
      value={value}
      onChange={(val) => {
        onChange(name, val);
        onBlur(name);
      }}
      placeholder="JJ/MM/AAAA"
      className={`w-full ${error && touched ? 'border-red-300' : ''}`}
    />
    {error && touched && (
      <p className="text-xs text-red-500 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        {error}
      </p>
    )}
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function CreateEmployeeModal({ open, onClose, onSuccess }) {
  const { token } = useAuth();
  const firstInputRef = useRef(null);
  
  // Form state
  const [form, setForm] = useState(INITIAL_FORM);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [createAnother, setCreateAnother] = useState(false);
  
  // Check if contract requires end date
  const requiresEndDate = FIXED_TERM_CONTRACTS.includes(form.contract_type);
  
  // Focus first input on open
  useEffect(() => {
    if (open && firstInputRef.current) {
      setTimeout(() => firstInputRef.current?.focus(), 100);
    }
  }, [open]);
  
  // Check if form has been modified
  const isFormDirty = useCallback(() => {
    return Object.entries(form).some(([key, value]) => {
      if (key === "department") return false;
      return value !== "";
    });
  }, [form]);
  
  // Validate a single field
  const validateField = useCallback((name, value) => {
    if (name === "end_date") {
      if (requiresEndDate && (!value || value.trim() === "")) {
        return VALIDATION_MESSAGES[name];
      }
      return null;
    }
    
    if (name === "department") {
      return null;
    }
    
    if (!value || value.trim() === "") {
      return VALIDATION_MESSAGES[name];
    }
    return null;
  }, [requiresEndDate]);
  
  // Update errors when form changes
  useEffect(() => {
    if (Object.keys(touched).length > 0) {
      const newErrors = {};
      Object.keys(touched).forEach(field => {
        if (touched[field]) {
          const error = validateField(field, form[field]);
          if (error) {
            newErrors[field] = error;
          }
        }
      });
      setErrors(newErrors);
    }
  }, [form, touched, validateField]);
  
  // Clear end_date when switching to non-fixed-term contract
  useEffect(() => {
    if (!requiresEndDate && form.end_date) {
      setForm(prev => ({ ...prev, end_date: "" }));
      setTouched(prev => ({ ...prev, end_date: false }));
    }
  }, [requiresEndDate, form.end_date]);
  
  // Check if form is valid
  const isFormValid = useCallback(() => {
    const requiredFields = ["first_name", "last_name", "job_title", "contract_type", "employee_status", "hire_date"];
    const allRequiredFilled = requiredFields.every(field => form[field] && form[field].trim() !== "");
    
    if (requiresEndDate) {
      return allRequiredFilled && form.end_date && form.end_date.trim() !== "";
    }
    
    return allRequiredFilled;
  }, [form, requiresEndDate]);
  
  // Handle field change
  const handleChange = useCallback((name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
  }, []);
  
  // Handle field blur (mark as touched)
  const handleBlur = useCallback((name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
  }, []);
  
  // Reset form
  const resetForm = () => {
    setForm(INITIAL_FORM);
    setTouched({});
    setErrors({});
  };
  
  // Handle close with confirmation if dirty
  const handleClose = () => {
    if (isFormDirty()) {
      setShowConfirmClose(true);
    } else {
      resetForm();
      onClose();
    }
  };
  
  // Confirm close
  const confirmClose = () => {
    resetForm();
    setShowConfirmClose(false);
    onClose();
  };
  
  // Handle submit
  const handleSubmit = async (andAnother = false) => {
    setCreateAnother(andAnother);
    
    // Mark all fields as touched
    const allTouched = {};
    Object.keys(form).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);
    
    // Validate
    if (!isFormValid()) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Build payload
      const payload = {
        personal_info: {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim().toUpperCase(),
        },
        professional_info: {
          job_title: form.job_title.trim(),
          department: form.department.trim() || null,
          contract_type: form.contract_type,
          employee_status: form.employee_status,
          hire_date: form.hire_date,
        },
        status: "active",
      };
      
      // Add contract_end_date for fixed-term contracts
      if (requiresEndDate && form.end_date) {
        payload.professional_info.contract_end_date = form.end_date;
      }
      
      const response = await axios.post(
        `${API}/hr/employees`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newEmployee = response.data;
      const fullName = `${form.first_name} ${form.last_name.toUpperCase()}`;
      
      toast.success("Salarié créé", {
        description: `${fullName} (${newEmployee.matricule}) a été ajouté avec succès.`
      });
      
      if (andAnother) {
        resetForm();
        setTimeout(() => firstInputRef.current?.focus(), 100);
        onSuccess?.(newEmployee, true);
      } else {
        resetForm();
        onClose();
        onSuccess?.(newEmployee, false);
      }
      
    } catch (error) {
      console.error("Error creating employee:", error);
      const message = error.response?.data?.detail || "Erreur lors de la création du salarié";
      toast.error("Erreur", { description: message });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      handleClose();
    }
  };
  
  // Prevent Enter from submitting prematurely
  const handleFormKeyDown = (e) => {
    if (e.key === "Enter" && e.target.tagName !== "BUTTON") {
      e.preventDefault();
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent 
          className="sm:max-w-[520px] w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto" 
          onKeyDown={handleKeyDown}
          onInteractOutside={(e) => {
            if (isFormDirty()) {
              e.preventDefault();
              setShowConfirmClose(true);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Nouveau salarié
            </DialogTitle>
            <DialogDescription className="text-sm">
              Renseignez les informations minimales.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 sm:space-y-6 py-3 sm:py-4" onKeyDown={handleFormKeyDown}>
            {/* Bloc 1 - Identité */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-xs sm:text-sm font-semibold text-slate-900 uppercase tracking-wide">
                Identité
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  ref={firstInputRef}
                  name="first_name"
                  label="Prénom"
                  value={form.first_name}
                  error={errors.first_name}
                  touched={touched.first_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Jean"
                />
                <FormField
                  name="last_name"
                  label="Nom"
                  value={form.last_name}
                  error={errors.last_name}
                  touched={touched.last_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="DUPONT"
                />
              </div>
              <p className="text-xs text-slate-400 flex items-center gap-1">
                <Info className="w-3 h-3" />
                Le nom sera automatiquement converti en majuscules
              </p>
            </div>
            
            {/* Bloc 2 - Poste */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-xs sm:text-sm font-semibold text-slate-900 uppercase tracking-wide">
                Poste
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  name="job_title"
                  label="Intitulé du poste"
                  value={form.job_title}
                  error={errors.job_title}
                  touched={touched.job_title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Développeur Senior"
                />
                <FormField
                  name="department"
                  label="Département"
                  value={form.department}
                  error={errors.department}
                  touched={touched.department}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="IT, RH, Commercial..."
                  required={false}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <SelectField
                  name="contract_type"
                  label="Type de contrat"
                  value={form.contract_type}
                  error={errors.contract_type}
                  touched={touched.contract_type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Sélectionner..."
                  options={[
                    { value: "cdi", label: "CDI" },
                    { value: "cdd", label: "CDD" },
                    { value: "alternance", label: "Alternance" },
                    { value: "stage", label: "Stage" },
                    { value: "interim", label: "Intérim" },
                  ]}
                />
                <SelectField
                  name="employee_status"
                  label="Statut"
                  value={form.employee_status}
                  error={errors.employee_status}
                  touched={touched.employee_status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Sélectionner..."
                  options={[
                    { value: "cadre", label: "Cadre" },
                    { value: "non_cadre", label: "Non-cadre" },
                    { value: "etam", label: "ETAM" },
                  ]}
                />
              </div>
            </div>
            
            {/* Bloc 3 - Dates */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-xs sm:text-sm font-semibold text-slate-900 uppercase tracking-wide">
                Dates
              </h3>
              <div className={`grid gap-3 sm:gap-4 ${requiresEndDate ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                <DateField
                  name="hire_date"
                  label="Date d'embauche"
                  value={form.hire_date}
                  error={errors.hire_date}
                  touched={touched.hire_date}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {requiresEndDate && (
                  <DateField
                    name="end_date"
                    label={`Date de fin ${form.contract_type === 'cdd' ? 'CDD' : form.contract_type === 'alternance' ? 'alternance' : 'stage'}`}
                    value={form.end_date}
                    error={errors.end_date}
                    touched={touched.end_date}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                )}
              </div>
            </div>
            
            {/* Info matricule */}
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-xs text-slate-500">
                <span className="font-medium text-slate-700">Matricule :</span> Généré automatiquement
              </p>
              <p className="text-xs text-slate-400 mt-1 hidden sm:block">
                Les autres informations pourront être complétées dans la fiche salarié.
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              data-testid="cancel-btn"
              className="w-full sm:w-auto"
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleSubmit(true)}
              disabled={!isFormValid() || isSubmitting}
              className="text-blue-600 border-blue-200 hover:bg-blue-50 w-full sm:w-auto hidden sm:inline-flex"
              data-testid="create-another-btn"
            >
              {isSubmitting && createAnother ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Créer et en ajouter un autre
            </Button>
            <Button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={!isFormValid() || isSubmitting}
              className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
              data-testid="create-btn"
            >
              {isSubmitting && !createAnother ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Créer le salarié
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Confirm close dialog */}
      <AlertDialog open={showConfirmClose} onOpenChange={setShowConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Abandonner la création ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous avez commencé à remplir le formulaire. Si vous fermez maintenant, 
              les informations saisies seront perdues.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuer la saisie</AlertDialogCancel>
            <AlertDialogAction onClick={confirmClose} className="bg-red-600 hover:bg-red-700">
              Abandonner
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
